import * as React from 'react'
import { Activity, BookOpen, Building2, CreditCard, FileText, Inbox, KeyRound, Plug, ScrollText, Settings, Users, Webhook } from 'lucide-react'
import { uuidParamSchema } from '@lib/validators/params'
import { useTenantAuth } from '@/auth/TenantAuthContext'
import { AppShell, type AppNavItem } from '@/components/AppShell'
import { EmptyState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Select } from '@/components/ui/select'
import { DashboardScopeProvider, type DashboardScopeValue } from '@/dashboard/DashboardScope'
import { ModeSwitch, TestModeBanner } from '@/dashboard/ModeSwitch'
import { useDashboardMode } from '@/dashboard/useDashboardMode'
import { useLeaveScope } from '@/dashboard/useLeaveScope'
import { tenantApi } from '@/helpers/apiClient'
import { useMcpEnabled } from '@/hooks/useConnectedApps'

/** The organization switcher, or the organization's name when there is only one. */
function OrganizationSwitcher() {
  const { session, activeMembership, setActiveTenant } = useTenantAuth()
  const leaveScope = useLeaveScope()
  const switchTenant = React.useMemo(() => leaveScope(setActiveTenant), [leaveScope, setActiveTenant])
  const memberships = session?.memberships ?? []

  function selectTenant(value: string): void {
    const parsed = uuidParamSchema.safeParse(value)
    if (!parsed.success) return
    const match = memberships.find((row) => row.tenantId === parsed.data)
    if (match) switchTenant(match.tenantId)
  }

  if (memberships.length > 1) {
    return (
      <Select
        aria-label="Organization"
        value={activeMembership?.tenantId ?? ''}
        onChange={(event) => selectTenant(event.target.value)}
      >
        {memberships.map((row) => (
          <option key={row.tenantId} value={row.tenantId}>
            {row.tenantName}
            {row.tenantStatus === 'suspended' ? ' (suspended)' : ''}
          </option>
        ))}
      </Select>
    )
  }
  if (!activeMembership) return null
  return (
    <div className="flex h-control items-center gap-2 rounded-md border border-border bg-card px-2.5 shadow-card">
      <Building2 className="size-4 shrink-0 text-muted-foreground" />
      <span className="truncate font-medium">{activeMembership.tenantName}</span>
      {activeMembership.tenantStatus === 'suspended' ? <Badge tone="warning">Suspended</Badge> : null}
    </div>
  )
}

/**
 * The tenant dashboard shell. Everything below is scoped to the active
 * organization through `DashboardScope`; switching organizations remounts the
 * page so queries start fresh. A suspended organization is read-only.
 */
export function TenantLayout() {
  const { session, activeMembership, logout, refresh } = useTenantAuth()
  const [mode, setStoredMode] = useDashboardMode(activeMembership?.tenantId ?? null)
  const leaveScope = useLeaveScope()
  const setMode = React.useMemo(() => leaveScope(setStoredMode), [leaveScope, setStoredMode])

  const scope: DashboardScopeValue | null = React.useMemo(() => {
    if (!activeMembership || !session) return null
    if (activeMembership.tenantStatus === 'suspended') {
      return {
        tenantId: activeMembership.tenantId,
        tenantName: activeMembership.tenantName,
        role: activeMembership.role,
        readOnly: true,
        readOnlyReason: 'suspended',
        currentUserId: session.user.id,
        client: tenantApi,
        cacheKey: 'tenant',
        mode,
        setMode,
        basePath: '',
        refreshSession: refresh,
      }
    }
    return {
      tenantId: activeMembership.tenantId,
      tenantName: activeMembership.tenantName,
      role: activeMembership.role,
      readOnly: false,
      readOnlyReason: null,
      currentUserId: session.user.id,
      client: tenantApi,
      cacheKey: 'tenant',
      mode,
      setMode,
      basePath: '',
      refreshSession: refresh,
    }
  }, [activeMembership, session, refresh, mode, setMode])

  const mcpEnabled = useMcpEnabled()
  const nav: AppNavItem[] = [
    { to: '/envelopes', label: 'Envelopes', icon: FileText },
    { to: '/team', label: 'Team', icon: Users },
    { to: '/api-keys', label: 'API keys', icon: KeyRound },
    { to: '/webhooks', label: 'Webhooks', icon: Webhook },
    { to: '/requests', label: 'API requests', icon: ScrollText },
    { to: '/settings', label: 'Settings', icon: Settings },
    { to: '/billing', label: 'Billing', icon: CreditCard },
    ...(mcpEnabled ? [{ to: '/connected-apps', label: 'Connected apps', icon: Plug }] : []),
    ...(mode === 'test' ? [{ to: '/mailbox', label: 'Mailbox', icon: Inbox }] : []),
    ...(activeMembership?.role === 'owner' ? [{ to: '/activity', label: 'Activity', icon: Activity }] : []),
    { to: '/docs', label: 'API docs', icon: BookOpen },
  ]

  const shell = (
    <AppShell
      homeTo="/envelopes"
      nav={nav}
      header={
        <div className="space-y-2">
          <OrganizationSwitcher />
          {scope ? <ModeSwitch mode={mode} onChange={setMode} /> : null}
        </div>
      }
      {...(scope && mode === 'test' ? { banner: <TestModeBanner /> } : {})}
      {...(session ? { userEmail: session.user.email } : {})}
      {...(activeMembership ? { outletKey: activeMembership.tenantId } : {})}
      onSignOut={logout}
    >
      {scope ? undefined : (
        <EmptyState
          icon={Building2}
          title="No organization yet"
          description="You don't belong to any organization on PixelSign. Ask an owner to invite you, or contact PixelSign support."
        />
      )}
    </AppShell>
  )
  return scope ? <DashboardScopeProvider value={scope}>{shell}</DashboardScopeProvider> : shell
}
